import {call, put, takeEvery, select, race, take} from 'redux-saga/effects';
import {push as redirect} from 'redux-first-history';

import {renderApplicationPath} from 'paths';
import {
    applicationTokenError,
    applicationTokenSet,
    getNewApplicationToken,
    setLoginState
} from 'modules/authentication/actions';
import {loginStates} from 'modules/authentication/constants';
import {pathSelector} from 'modules/rootSelectors';
import {doApiCall} from 'modules/sagaUtils';
import {notifyUser} from 'modules/toast/actions';

import {
    applicationsError,
    getUserApplications,
    receiveApplications,
    resetApps,
    selectApplication,
    setApps,
    setErrorFlag,
    setSelectedApplication,
    setTokenStatus
} from './actions';
import {selectedApplicationSelector} from './selectors';
import {setApplicationBasePath, setApplicationResourceRoot} from './utils';
import {setTitle} from 'modules/a11ycat/actions';
import {getApplicationToken} from 'modules/authentication/utils';

export default function* () {
    console.log('Starting applications saga');
    yield takeEvery(setLoginState, handleLoginStateChange);
    yield takeEvery(selectApplication, handleSelectApplication)
}

let currentLoginState = loginStates.ENTER_USERNAME;

function* handleLoginStateChange(action) {
    const newLoginState = action.payload || loginStates.ENTER_USERNAME;

    if (newLoginState == currentLoginState) {
        console.log('No change', newLoginState, currentLoginState);
        return;
    }

    currentLoginState = newLoginState;

    if (newLoginState != loginStates.LOGGED_IN) {
        yield put(resetApps());
        return;
    }

    let appData;
    try {
        const apiAction = yield call(
            doApiCall,
            getUserApplications,
            null,
            null,
            receiveApplications,
            applicationsError,
        );

        appData = apiAction.payload.data;

    } catch (e) {
        console.error(e);
        yield put(setErrorFlag());
        return;
    }

    const apps = Object
        .entries(appData)
        .map(([key, data]) => ({
            id: key,
            name: data.name,
            slug: data.slug,
            type: data.type,
            image: { card: data.image },
            resourceRoot: data.resourceRoot,
            link: data.link,
            lastAccessed: data.lastAccessed,
        }))
        .filter(app => app.type !== 'api'); // remove apps of type 'api' as they do not have a UI

    yield put(setApps(apps));

    if (apps.length == 1) {
        yield put(selectApplication(apps[0].id));
    }
}

function* handleSelectApplication(selected) {
    let app = yield select(selectedApplicationSelector);

    if (app && app.id == selected.payload) {
        console.log('application already selected');
        return;
    }

    yield put(setTokenStatus(false));
    yield put(setSelectedApplication(selected.payload));
    app = yield select(selectedApplicationSelector);

    if (!app || !app.name) {
        yield put(notifyUser('Application not found', 'error'));
        return;
    }

    const token = getApplicationToken(app.slug);
    if (!token) {
        // get a new application token
        yield put(getNewApplicationToken(app.slug));

        const result = yield race({
            success: take(applicationTokenSet),
            error: take(applicationTokenError)
        })

        if (result.error) {
            yield put(setSelectedApplication(null));
            yield put(notifyUser('There was an error when accessing the application', 'error'))
            return;
        }

    }

    setApplicationBasePath(app.slug);
    setApplicationResourceRoot(app.slug, app.resourceRoot);

    yield put(setTokenStatus(true));
    yield put(setTitle(app.name));

    const path = yield select(pathSelector);
    const newPath = renderApplicationPath(app.slug);
    if (!path.startsWith(newPath)) {
        yield put(redirect(newPath));
    }
}
